import { FFmpeg } from '@ffmpeg/ffmpeg';
import { LogEvent, ProgressEvent } from '@ffmpeg/ffmpeg/dist/esm/types';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
import {
  blobToFile2,
  blobToURL,
  getExtensionFileName,
  getFileNameWithoutExtension,
} from '@vindr/utils/converter';
import { floorDecimals } from '@vindr/utils/helpers/common.helper';
import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { ffmpegOptionByExtension } from './webcam-helper';

export const useEncoder = (conditionLoad?: boolean) => {
  const [info, setInfo] = useState<TEncoderJob>();
  const [loaded, setLoaded] = useState(false);

  const ffmpeg = useMemo(() => {
    const ffmpeg = new FFmpeg();
    ffmpeg.on('log', ({ message, type }: LogEvent): void => {
      // console.info(`[FFMPEG][${type}] ${message}`);
      /* setInfo((job) => {
        const isStatsRow = Boolean(message.match(/^(frame=|size=)/));
        if (isStatsRow) {
          const frames = Number(message.match(/frame= *(\d+)(?: |$)/)?.[1] ?? '');
          const fps = parseFloat(message.match(/fps=(.*?)(?: |$)/)?.[1] ?? '');
          const targetFPS = parseFloat(message.match(/q=(.*?)(?: |$)/)?.[1] ?? '');
          const sizeKB = parseFloat(message.match(/size=(.*?)kB/)?.[1] ?? '');
          // const time = message.match(/time=(.*?)(?: |$)/)?.[1] ?? null; // Unused
          const bitrateKBPS = parseFloat(message.match(/bitrate=(.*?)kbits\/s/)?.[1] ?? '');
          const dup = Number(message.match(/dup= *(\d+)(?: |$)/)?.[1] ?? '');
          const drop = Number(message.match(/drop= *(\d+)(?: |$)/)?.[1] ?? '');
          // const speed = parseFloat(message.match(/speed=(.*?)x(?: |$)/)?.[1] ?? ""); // Unused
          // const throttle = message.match(/throttle=(.*?)(?: |$)/)?.[1] ?? null; // Unused
          return {
            ...job,
            progressStats: {
              ...job?.progressStats,
              frames: Number.isNaN(frames) ? undefined : frames,
              size: Number.isNaN(sizeKB) ? 0 : sizeKB * 1000,
              fps: Number.isNaN(fps) ? undefined : fps,
              bitrate: Number.isNaN(bitrateKBPS) ? undefined : bitrateKBPS * 1000,
              framerate: Number.isNaN(targetFPS) ? undefined : targetFPS,
              dup: Number.isNaN(dup) ? 0 : dup,
              drop: Number.isNaN(drop) ? 0 : drop,
            },
          };
        }

        const isEndStatsRow = Boolean(message.match(/^(video:)/));
        if (isEndStatsRow) {
          const videoKB = Number(message.match(/video:(\d+?)kB/)?.[1] ?? '');
          const audioKB = Number(message.match(/audio:(\d+?)kB/)?.[1] ?? '');
          const subtitlesKB = Number(message.match(/subtitle:(\d+?)kB/)?.[1] ?? '');
          const otherKB = Number(message.match(/other streams:(\d+?)kB/)?.[1] ?? '');
          const headersKB = Number(message.match(/global headers:(\d+?)kB/)?.[1] ?? '');
          const muxingPercent = parseFloat(message.match(/muxing overhead: *(.+?)%/)?.[1] ?? '');
          return {
            ...job,
            endStats: {
              ...job.endStats,
              videoSize: Number.isNaN(videoKB) ? 0 : videoKB * 1000,
              audioSize: Number.isNaN(audioKB) ? 0 : audioKB * 1000,
              subtitlesSize: Number.isNaN(subtitlesKB) ? 0 : subtitlesKB * 1000,
              otherSize: Number.isNaN(otherKB) ? 0 : otherKB * 1000,
              headersSize: Number.isNaN(headersKB) ? 0 : headersKB * 1000,
              muxingAmount: Number.isNaN(muxingPercent) ? 0 : muxingPercent / 100,
            },
          };
        }

        return job;
      }); */
    });
    ffmpeg.on('progress', ({ progress, time }: ProgressEvent): void => {
      const calProgress = floorDecimals(progress * 100, 2);
      const calTime = floorDecimals(time / 1000 / 1000, 2);
      // console.info(`${calProgress}% | ${calTime}ms`);

      setInfo((prev) => ({
        ...prev,

        progress: calProgress,
        time: calTime,
      }));
    });
    return ffmpeg;
  }, []);

  useLayoutEffect(() => {
    const baseURL = 'ffmpeg';
    if (ffmpeg.loaded) {
      setLoaded(false);
      return;
    }

    if (typeof conditionLoad === 'boolean' && !conditionLoad) {
      setLoaded(false);
      return;
    }

    (async () => {
      await ffmpeg.load({
        coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
        workerURL: await toBlobURL(`${baseURL}/ffmpeg-core.worker.js`, 'text/javascript'),
      });
      setLoaded(true);
    })();

    return () => {
      ffmpeg.terminate();
    };
  }, [ffmpeg]);

  const convert = useCallback(
    async ({ file, name, id }: { file: string | File | Blob; name: string; id?: string }) => {
      if (!ffmpeg.loaded) {
        setLoaded(ffmpeg.loaded);
        return;
      }

      setInfo((prev) => ({
        ...prev,
        name: name,
        ...(id && { id }),
      }));

      const extension = getExtensionFileName(name) || 'default';

      await ffmpeg.writeFile(name, await fetchFile(file));
      const outputFileName = `${getFileNameWithoutExtension(name)}.mp4`;
      await ffmpeg.exec([
        '-i',
        name,
        ...(ffmpegOptionByExtension[extension] || ffmpegOptionByExtension['default']),
        outputFileName,
      ]);
      const fileData = await ffmpeg.readFile(outputFileName);
      const data = new Uint8Array(fileData as ArrayBuffer);

      const blob = new Blob([data.buffer], { type: 'video/mp4' });

      return {
        objectURL: blobToURL(blob),
        renderFile: blobToFile2(blob, outputFileName),
        buffer: [data.buffer],
        outputFileName,
        type: 'video/mp4;codecs=h264',
      };
    },
    [ffmpeg.loaded]
  );

  return {
    info,
    convert,
    loaded,
  };
};

/* const loadFFmpeg = async (
  ffmpegRef: MutableRefObject<FFmpeg>,
  onLog?: (val: LogEvent) => void,
  onProgress?: (val: ProgressEvent) => void
) => {
  const baseURL = 'ffmpeg';
  const ffmpeg = ffmpegRef.current;

  if (ffmpeg.loaded) return;

  ffmpeg.on('log', onLog);
  ffmpeg.on('progress', onProgress);

  await ffmpeg.load({
    coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
    wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
    workerURL: await toBlobURL(`${baseURL}/ffmpeg-core.worker.js`, 'text/javascript'),
  });
}; */

export type TEncoderJob = {
  id: string;
  name: string;
  progress: number;
  time?: number;
  progressStats?: {
    time: number;
    frames?: number;
    fps?: number;
    size: number; // In bytes
    bitrate?: number; // In bits per second
    framerate?: number;
    dup: number;
    drop: number;
  };
  endStats?: {
    videoSize: number;
    audioSize: number;
    subtitlesSize: number;
    otherSize: number;
    headersSize: number;
    muxingAmount: number;
  };
};
